/** What the 5/50 EMA engine is doing right now. Polls every 5s; read-only.
 *
 * Ordered by what changes a decision. The open position leads because its stop
 * is the only number that matters once a trade exists; a resting limit comes
 * next because it is a trade about to exist; the market reads sit last, since
 * they are context rather than a call to act.
 *
 * ⚠️ Everything here is INDEX POINTS on the underlying, never option premium.
 */

import { type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  emaTrendApi,
  type EmaTrendPending,
  type EmaTrendPosition,
  type EmaTrendStatus,
} from "@/lib/emaTrendApi";

const POLL_MS = 5_000;

export function EmaTrendStatusCard({ strategyId }: Readonly<{ strategyId: string }>): ReactElement {
  const q = useQuery({
    queryKey: ["ema-trend-status", strategyId],
    queryFn: () => emaTrendApi.status(strategyId),
    refetchInterval: POLL_MS,
  });
  return (
    <section className="card mb-3">
      <div className="mb-2 flex items-center gap-2">
        <i className="ti ti-chart-line text-accent" />
        <span className="text-sm font-medium">5/50 EMA engine</span>
        {q.data?.tracking === true && <Chips status={q.data} />}
      </div>
      <Body data={q.data} loading={q.isPending} failed={q.isError} />
    </section>
  );
}

export function Body({
  data,
  loading,
  failed,
}: Readonly<{
  data: EmaTrendStatus | undefined;
  loading: boolean;
  failed: boolean;
}>): ReactElement {
  if (loading) return <p className="text-xs text-ink-muted">Reading the engine…</p>;
  if (failed || data === undefined) {
    return <p className="text-xs text-ink-muted">Could not read the engine&apos;s status.</p>;
  }
  if (!data.tracking) return <NotTracking />;
  return (
    <div className="flex flex-col gap-2">
      {data.position !== null && <PositionRow position={data.position} />}
      {data.pending !== null && <PendingRow order={data.pending} />}
      {data.position === null && data.pending === null && (
        <p className="text-xs text-ink-muted">
          Flat — waiting for a 50 EMA cross with structure, or a stretched 5 EMA cross against it.
        </p>
      )}
      <MarketRow status={data} />
      <TickRow at={data.last_tick_at} />
    </div>
  );
}

/** The sharp edge of this card. A restart empties the runtime but does NOT
 * close a position, so "not tracking" must never be shown as "flat". */
function NotTracking(): ReactElement {
  return (
    <p className="text-xs text-ink-muted">
      Not tracking — the engine has not ticked for this strategy since the last restart. This is not
      the same as flat: a restart forgets an open position without closing it, so check the trade
      log before assuming there is nothing running.
    </p>
  );
}

function Chips({ status }: Readonly<{ status: EmaTrendStatus }>): ReactElement {
  return (
    <span className="ml-auto flex items-center gap-1">
      {status.structure !== null && <Chip text={`structure ${status.structure.toLowerCase()}`} />}
      {status.position !== null && (
        <Chip text={status.position.armed ? "trail armed" : "stop only"} accent />
      )}
    </span>
  );
}

function Chip({
  text,
  accent = false,
}: Readonly<{ text: string; accent?: boolean }>): ReactElement {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] ${
        accent ? "bg-accent/10 text-accent" : "bg-surface-2 text-ink-muted"
      }`}
    >
      {text}
    </span>
  );
}

function PositionRow({ position }: Readonly<{ position: EmaTrendPosition }>): ReactElement {
  return (
    <div className="rounded-ctl bg-surface-2 px-2 py-1.5 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium text-accent">
          {position.case} {position.side}
        </span>
        {position.shape !== null && <Chip text={shapeLabel(position.shape)} />}
        <Figure label="entry" value={position.entry} />
        <Figure label="stop" value={position.stop} />
        <Figure label="risk" value={position.risk} />
        <Figure label="best" value={position.best} />
      </div>
      <p className="mt-1 text-[11px] text-ink-muted">
        {exitsFor(position)}
        {position.contract_symbol !== null && ` Holding ${position.contract_symbol}.`}
      </p>
    </div>
  );
}

/** Which exits are actually live. Below State 2 the trail and the reversal's
 * 50 EMA target are both dead, so saying "trailing" there would be a lie. */
function exitsFor(position: EmaTrendPosition): string {
  if (!position.armed) {
    return "Below State 2 — the stop is the only exit; the trail is not armed yet.";
  }
  return position.case === "REVERSAL"
    ? "State 2 reached — exits on a 50 EMA touch or a 5 EMA close."
    : "State 2 reached — exits on a 5 EMA close.";
}

/** The two signal-candle shapes a reversal can arm on (engine `Shape`). Kept
 * apart on the card and in the trade log's signal source so the paths can be
 * analysed separately (operator, 2026-09-09). */
function shapeLabel(shape: string): string {
  if (shape === "CLEAR") return "clear of 5 EMA";
  if (shape === "BODY") return "body-dominant";
  return shape.toLowerCase();
}

function PendingRow({ order }: Readonly<{ order: EmaTrendPending }>): ReactElement {
  const left = `${String(order.candles_left)} candle${order.candles_left === 1 ? "" : "s"} left`;
  return (
    <div className="rounded-ctl bg-surface-2 px-2 py-1.5 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium">
          {order.fills_on_break ? "trigger" : "limit"} · {order.case} {order.side}
        </span>
        {order.shape !== null && <Chip text={shapeLabel(order.shape)} />}
        <Figure label="at" value={order.limit} />
        <Figure label="stop" value={order.stop} />
        <Figure label="risk" value={order.risk} />
      </div>
      <p className="mt-1 text-[11px] text-ink-muted">
        {order.fills_on_break
          ? `Armed on the signal candle — fills when price breaks through the trigger. ${left} to break it.`
          : `Risk exceeded the cap, so the rule is waiting for a better price rather than taking it wide. ${left} to fill.`}
      </p>
    </div>
  );
}

function MarketRow({ status }: Readonly<{ status: EmaTrendStatus }>): ReactElement {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
      <Figure label="spot" value={status.last_spot} />
      <Figure label="50 EMA" value={status.slow_ema} />
      <Figure label="VIX" value={status.vix} digits={2} />
    </div>
  );
}

/** Index points, and never a zero standing in for a missing read — a zero on a
 * stop distance reads as a valid very tight stop. */
function Figure({
  label,
  value,
  digits = 2,
}: Readonly<{ label: string; value: number | null; digits?: number }>): ReactElement {
  return (
    <span className="text-ink-secondary">
      <span className="text-ink-muted">{label} </span>
      <span className="font-mono tabular-nums">{value === null ? "—" : value.toFixed(digits)}</span>
    </span>
  );
}

/** The answer to "is it running?", which an empty decision trail does NOT
 * answer — a quiet hour records nothing at all. */
function TickRow({ at }: Readonly<{ at: string | null }>): ReactElement {
  if (at === null) return <p className="text-[11px] text-ink-muted">No tick recorded yet.</p>;
  return (
    <p className="text-[11px] text-ink-muted">
      Last tick{" "}
      {new Date(at).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: "Asia/Kolkata",
      })}{" "}
      IST.
    </p>
  );
}
